/**
 * Analytics Log Remapper
 * 
 * Converts between AnalyticsLogDto (Firestore) and AnalyticsLogEntity (domain).
 */

import type { AnalyticsLogDto } from '@/data/dtos/analytics/analyticsLogDto';
import type { JsonObject } from '@/data/types/json';
import type { AnalyticsLogEntity } from '@/domain/entities/analytics/analyticsLogEntity';

// Coerce optional string fields read from Firestore. The DTO type promises
// `string | undefined`, but a bad external writer could push a number or
// other primitive into the doc — coercion keeps the entity contract honest.
function asOptString(v: unknown): string | undefined {
  if (v === undefined || v === null) return undefined;
  return typeof v === 'string' ? v : String(v);
}

export class AnalyticsLogRemapper {
  /**
   * Convert DTO to Entity
   */
  static toEntity(dto: AnalyticsLogDto): AnalyticsLogEntity {
    return {
      level: dto.level,
      userId: asOptString(dto.user_id) ?? null,
      message: asOptString(dto.message) ?? '',
      source: asOptString(dto.source) ?? '',
      context: dto.context,
      stackTrace: asOptString(dto.stack_trace),
      origin: asOptString(dto.origin),
      fingerprint: asOptString(dto.fingerprint),
      errorName: asOptString(dto.error_name),
      errorCode: asOptString(dto.error_code),
      deviceInfo: dto.device_info ? {
        userAgent: asOptString(dto.device_info.user_agent) ?? '',
        platform: asOptString(dto.device_info.platform) ?? '',
        screenSize: asOptString(dto.device_info.screen_size) ?? '',
        isMobile: dto.device_info.is_mobile,
        isIos: dto.device_info.is_ios,
      } : undefined,
      timestamp: dto.timestamp,
      timestampString: asOptString(dto.timestamp_string) ?? '',
      sessionId: asOptString(dto.session_id) ?? '',
      appVersion: asOptString(dto.app_version) ?? '',
    };
  }

  /**
   * Convert DTO to JSON for the data layer. The boundary cast lives here so
   * `analyticsService.flush` can pass DTOs to `firestoreService.batchWrite`
   * without each call site re-casting through `unknown`.
   */
  static toJson(dto: AnalyticsLogDto): JsonObject {
    return { ...dto } as JsonObject;
  }

  static toJsonList(dtos: AnalyticsLogDto[]): JsonObject[] {
    return dtos.map((dto) => this.toJson(dto));
  }

  /**
   * Convert Entity to DTO
   */
  static toDto(entity: AnalyticsLogEntity): AnalyticsLogDto {
    return {
      level: entity.level,
      user_id: entity.userId,
      message: entity.message,
      source: entity.source,
      context: entity.context,
      stack_trace: entity.stackTrace,
      origin: entity.origin,
      fingerprint: entity.fingerprint,
      error_name: entity.errorName,
      error_code: entity.errorCode,
      device_info: entity.deviceInfo ? {
        user_agent: entity.deviceInfo.userAgent,
        platform: entity.deviceInfo.platform,
        screen_size: entity.deviceInfo.screenSize,
        is_mobile: entity.deviceInfo.isMobile,
        is_ios: entity.deviceInfo.isIos,
      } : undefined,
      timestamp: entity.timestamp,
      timestamp_string: entity.timestampString,
      session_id: entity.sessionId,
      app_version: entity.appVersion,
    };
  }
}
